import { useEffect, useState } from "react";
import { Geolocation } from "@capacitor/geolocation";
import API from "../api";

function Lodges() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";

  const emptyForm = {
    name: "",
    city: "",
    address: "",
    latitude: "",
    longitude: "",
    meeting_day: "",
    contact_email: "",
    obedience: "",
    notes: "",
  };

  const [lodges, setLodges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [positionError, setPositionError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState("nearby");
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadNearbyLodges();
  }, []);

  const resetForm = () => setForm(emptyForm);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const autoFillCoordinates = async () => {
    if (!form.city.trim()) {
      alert("Renseigne d'abord un orient");
      return;
    }

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(
          form.city + ", France"
        )}`
      );

      const data = await res.json();

      if (!data || data.length === 0) {
        alert("Orient introuvable");
        return;
      }

      setForm((prev) => ({
        ...prev,
        latitude: data[0].lat,
        longitude: data[0].lon,
      }));

      alert("Coordonnées approximatives ajoutées");
    } catch (err) {
      console.error("Erreur géocodage:", err);
      alert("Impossible de récupérer les coordonnées");
    }
  };

  const loadNearbyLodges = async () => {
    try {
      setMode("nearby");
      setLoading(true);
      setPositionError("");

      const permission = await Geolocation.requestPermissions();

      if (permission.location !== "granted") {
        setPositionError("Permission refusée — affichage sans distance.");
        await loadAllLodges();
        return;
      }

      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
      });

      const { latitude, longitude } = position.coords;

      const res = await API.get(
        `/lodges/nearby?lat=${latitude}&lng=${longitude}`
      );

      setLodges(res.data);
    } catch (err) {
      console.error("Erreur géolocalisation:", err);
      setPositionError("Position indisponible — affichage sans distance.");
      await loadAllLodges();
    } finally {
      setLoading(false);
    }
  };

  const loadAllLodges = async () => {
    try {
      const res = await API.get("/lodges");
      setLodges(res.data);
    } catch (err) {
      console.error("Erreur lodges:", err);
      alert("Impossible de charger les loges");
    }
  };

  const searchLodges = async () => {
    const q = search.trim();

    if (q.length < 2) {
      alert("Entre au moins 2 caractères");
      return;
    }

    try {
      setMode("search");
      setLoading(true);
      setPositionError("");

      const res = await API.get(`/lodges/search?q=${encodeURIComponent(q)}`);
      setLodges(res.data);
    } catch (err) {
      console.error("Erreur recherche loges:", err);
      alert("Impossible de rechercher les loges");
    } finally {
      setLoading(false);
    }
  };

  const addLodge = async () => {
    if (!form.name.trim() || !form.city.trim()) {
      alert("Nom et orient obligatoires");
      return;
    }

    try {
      await API.post("/lodges", {
        ...form,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
      });

      resetForm();
      setShowForm(false);
      await loadNearbyLodges();
      alert("Loge ajoutée");
    } catch (err) {
      console.error("Erreur ajout loge:", err);
      alert("Impossible d'ajouter la loge");
    }
  };

  const deleteLodge = async (id) => {
    if (!window.confirm("Supprimer cette loge ?")) return;

    try {
      await API.delete(`/lodges/${id}`);

      if (mode === "search") {
        await searchLodges();
      } else {
        await loadNearbyLodges();
      }
    } catch (err) {
      console.error("Erreur suppression loge:", err);
      alert("Impossible de supprimer la loge");
    }
  };

  const formatDistance = (distance) => {
    if (distance === undefined || distance === null) return null;

    const d = Number(distance);
    if (Number.isNaN(d)) return null;

    if (d < 1) return `${Math.round(d * 1000)} m`;
    return `${d.toFixed(1)} km`;
  };

  const contactLodge = (email) => {
    if (!email) {
      alert("Aucun email de contact renseigné");
      return;
    }

    window.location.href = `mailto:${email}`;
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <p style={styles.small}>Autour de moi</p>
          <h1 style={styles.title}>Loges</h1>
          <div style={styles.goldLine} />
        </div>

        <button style={styles.locateButton} onClick={loadNearbyLodges}>
          📍
        </button>
      </div>

      <div style={styles.privacyBox}>
        🔒 Pour des raisons de confidentialité, l’adresse exacte des loges est
        gardée confidentielle. Pour plus d’informations, rapprochez-vous du
        secrétaire de la loge concernée.
      </div>

      <div style={styles.searchBox}>
        <p style={styles.searchTitle}>Préparer une visite</p>

        <div style={styles.searchRow}>
          <input
            style={styles.searchInput}
            placeholder="Orient ou nom de loge"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") searchLodges();
            }}
          />

          <button style={styles.searchButton} onClick={searchLodges}>
            🔎
          </button>
        </div>

        <button style={styles.nearbyButton} onClick={loadNearbyLodges}>
          Utiliser ma position actuelle
        </button>
      </div>

      {positionError && <div style={styles.warningBox}>{positionError}</div>}

      {isAdmin && (
        <button
          style={styles.adminButton}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "Fermer l’ajout" : "+ Ajouter une loge"}
        </button>
      )}

      {isAdmin && showForm && (
        <div style={styles.formCard}>
          <h2 style={styles.formTitle}>Nouvelle loge</h2>

          <input
            style={styles.input}
            placeholder="Nom de la loge"
            value={form.name}
            onChange={(e) => updateForm("name", e.target.value)}
          />

          <div style={styles.geoRow}>
            <input
              style={{ ...styles.input, marginBottom: 0, flex: 1 }}
              placeholder="Orient"
              value={form.city}
              onChange={(e) => updateForm("city", e.target.value)}
            />

            <button style={styles.geoButton} onClick={autoFillCoordinates}>
              📍
            </button>
          </div>

          <input
            style={styles.input}
            placeholder="Adresse exacte confidentielle"
            value={form.address}
            onChange={(e) => updateForm("address", e.target.value)}
          />

          <div style={styles.gridTwo}>
            <input
              style={styles.input}
              placeholder="Latitude approx."
              value={form.latitude}
              onChange={(e) => updateForm("latitude", e.target.value)}
            />

            <input
              style={styles.input}
              placeholder="Longitude approx."
              value={form.longitude}
              onChange={(e) => updateForm("longitude", e.target.value)}
            />
          </div>

          <input
            style={styles.input}
            placeholder="Jour de tenue"
            value={form.meeting_day}
            onChange={(e) => updateForm("meeting_day", e.target.value)}
          />

          <input
            style={styles.input}
            placeholder="Email de contact"
            value={form.contact_email}
            onChange={(e) => updateForm("contact_email", e.target.value)}
          />

          <input
            style={styles.input}
            placeholder="Obédience"
            value={form.obedience}
            onChange={(e) => updateForm("obedience", e.target.value)}
          />

          <textarea
            style={styles.textarea}
            placeholder="Notes visibles"
            value={form.notes}
            onChange={(e) => updateForm("notes", e.target.value)}
          />

          <button style={styles.goldButton} onClick={addLodge}>
            Enregistrer la loge
          </button>
        </div>
      )}

      <div style={styles.resultHeader}>
        <span style={styles.resultTitle}>
          {mode === "search" ? "Résultats de recherche" : "Loges proches"}
        </span>
        <span style={styles.resultCount}>{lodges.length}</span>
      </div>

      {loading ? (
        <p style={styles.empty}>Recherche des loges...</p>
      ) : lodges.length === 0 ? (
        <p style={styles.empty}>Aucune loge trouvée.</p>
      ) : (
        <div style={styles.list}>
          {lodges.map((lodge) => (
            <div key={lodge.id} style={styles.card}>
              <div style={styles.cardTop}>
                <div style={styles.iconBox}>⟐</div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2 style={styles.lodgeName}>{lodge.name}</h2>
                  <p style={styles.city}>📍 Orient de {lodge.city}</p>
                </div>

                {formatDistance(lodge.distance) && (
                  <div style={styles.distance}>
                    {formatDistance(lodge.distance)}
                  </div>
                )}
              </div>

              <div style={styles.infoBox}>
                {lodge.obedience && (
                  <p style={styles.info}>⚜️ {lodge.obedience}</p>
                )}

                {lodge.meeting_day && (
                  <p style={styles.info}>🕯️ {lodge.meeting_day}</p>
                )}

                <p style={styles.confidential}>
                  🔒 Adresse exacte confidentielle
                </p>

                {lodge.notes && <p style={styles.notes}>{lodge.notes}</p>}
              </div>

              <div style={styles.actions}>
                <button
                  style={styles.goldButton}
                  onClick={() => contactLodge(lodge.contact_email)}
                >
                  Contacter
                </button>

                <button
                  style={styles.secondaryButton}
                  onClick={() =>
                    alert(
                      "Pour organiser une visite, rapprochez-vous du secrétaire de la loge concernée."
                    )
                  }
                >
                  Visiter
                </button>
              </div>

              {isAdmin && (
                <button
                  style={styles.deleteButton}
                  onClick={() => deleteLodge(lodge.id)}
                >
                  Supprimer
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: "24px",
    paddingBottom: "115px",
    background: "linear-gradient(180deg, #090d15 0%, #1a1008 100%)",
    color: "#fff7e8",
    boxSizing: "border-box",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 18,
  },

  small: {
    margin: 0,
    color: "#d6b46a",
    fontSize: 16,
    fontWeight: 900,
  },

  title: {
    margin: "2px 0 0",
    fontSize: 42,
    lineHeight: 1,
    fontWeight: 900,
  },

  goldLine: {
    width: 48,
    height: 4,
    borderRadius: 999,
    background: "#d6b46a",
    marginTop: 10,
  },

  locateButton: {
    width: 58,
    height: 58,
    borderRadius: "50%",
    border: "1px solid rgba(214,180,106,0.55)",
    background: "rgba(0,0,0,0.38)",
    color: "#d6b46a",
    fontSize: 26,
    boxShadow: "0 0 26px rgba(214,180,106,0.28)",
  },

  privacyBox: {
    background: "rgba(214,180,106,0.1)",
    border: "1px solid rgba(214,180,106,0.26)",
    color: "#f2d99a",
    borderRadius: 18,
    padding: 13,
    marginBottom: 14,
    fontSize: 14,
    lineHeight: 1.45,
  },

  searchBox: {
    background: "rgba(9,14,22,0.88)",
    border: "1px solid rgba(214,180,106,0.24)",
    borderRadius: 22,
    padding: 14,
    marginBottom: 14,
    boxShadow: "0 14px 32px rgba(0,0,0,0.28)",
  },

  searchTitle: {
    margin: "0 0 10px",
    color: "#d6b46a",
    fontSize: 17,
    fontWeight: 900,
  },

  searchRow: {
    display: "flex",
    gap: 10,
  },

  searchInput: {
    flex: 1,
    minWidth: 0,
    padding: 14,
    borderRadius: 15,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(0,0,0,0.35)",
    color: "#fff",
    fontSize: 16,
    boxSizing: "border-box",
  },

  searchButton: {
    width: 54,
    borderRadius: 15,
    border: "none",
    background: "linear-gradient(180deg, #e4c46f, #c99f43)",
    color: "#101010",
    fontSize: 20,
    fontWeight: 900,
  },

  nearbyButton: {
    width: "100%",
    marginTop: 10,
    padding: 12,
    borderRadius: 15,
    border: "1px solid rgba(214,180,106,0.35)",
    background: "rgba(255,255,255,0.06)",
    color: "#f4d98d",
    fontSize: 15,
    fontWeight: 900,
  },

  warningBox: {
    background: "rgba(255,184,77,0.13)",
    border: "1px solid rgba(255,184,77,0.28)",
    color: "#ffd99b",
    borderRadius: 18,
    padding: 13,
    marginBottom: 14,
    fontSize: 14,
    lineHeight: 1.4,
  },

  adminButton: {
    width: "100%",
    padding: 14,
    marginBottom: 16,
    borderRadius: 16,
    border: "1px solid rgba(214,180,106,0.45)",
    background: "rgba(214,180,106,0.12)",
    color: "#f4d98d",
    fontSize: 16,
    fontWeight: 900,
  },

  formCard: {
    background: "rgba(9,14,22,0.88)",
    border: "1px solid rgba(214,180,106,0.35)",
    borderRadius: 24,
    padding: 16,
    marginBottom: 20,
    boxShadow: "0 18px 40px rgba(0,0,0,0.38)",
  },

  formTitle: {
    margin: "0 0 14px",
    color: "#d6b46a",
    fontSize: 24,
  },

  input: {
    width: "100%",
    padding: 14,
    marginBottom: 12,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(0,0,0,0.35)",
    color: "#fff",
    fontSize: 16,
    boxSizing: "border-box",
  },

  geoRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    marginBottom: 12,
  },

  geoButton: {
    width: 54,
    height: 54,
    borderRadius: 16,
    border: "none",
    background: "linear-gradient(180deg, #e4c46f, #c99f43)",
    color: "#101010",
    fontSize: 22,
    fontWeight: 900,
    flexShrink: 0,
  },

  textarea: {
    width: "100%",
    minHeight: 100,
    padding: 14,
    marginBottom: 12,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(0,0,0,0.35)",
    color: "#fff",
    fontSize: 16,
    lineHeight: 1.5,
    boxSizing: "border-box",
  },

  gridTwo: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  },

  resultHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "14px 2px 12px",
  },

  resultTitle: {
    color: "#d6b46a",
    fontSize: 17,
    fontWeight: 900,
  },

  resultCount: {
    minWidth: 28,
    height: 28,
    borderRadius: 999,
    background: "rgba(214,180,106,0.18)",
    border: "1px solid rgba(214,180,106,0.35)",
    color: "#f4d98d",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 900,
  },

  list: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },

  card: {
    background: "rgba(9,14,22,0.88)",
    border: "1px solid rgba(255,255,255,0.11)",
    borderRadius: 26,
    padding: 17,
    boxShadow: "0 18px 40px rgba(0,0,0,0.36)",
  },

  cardTop: {
    display: "flex",
    alignItems: "center",
    gap: 13,
    marginBottom: 14,
  },

  iconBox: {
    width: 54,
    height: 54,
    borderRadius: 16,
    border: "1px solid rgba(214,180,106,0.4)",
    background: "rgba(214,180,106,0.12)",
    color: "#d6b46a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 30,
    fontWeight: 900,
    boxShadow: "0 0 18px rgba(214,180,106,0.18)",
    flexShrink: 0,
  },

  lodgeName: {
    margin: 0,
    fontSize: 22,
    fontWeight: 900,
    color: "#ffffff",
    lineHeight: 1.1,
  },

  city: {
    margin: "5px 0 0",
    color: "#f0dfbd",
    fontSize: 15,
  },

  distance: {
    padding: "7px 10px",
    borderRadius: 999,
    background: "rgba(214,180,106,0.16)",
    border: "1px solid rgba(214,180,106,0.35)",
    color: "#f4d98d",
    fontSize: 13,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  infoBox: {
    background: "rgba(0,0,0,0.22)",
    borderRadius: 18,
    padding: "10px 13px",
    marginBottom: 14,
  },

  info: {
    margin: "7px 0",
    fontSize: 15,
    color: "#f0dfbd",
    lineHeight: 1.4,
  },

  confidential: {
    margin: "9px 0 0",
    padding: 10,
    borderRadius: 12,
    background: "rgba(255, 200, 100, 0.08)",
    border: "1px solid rgba(255, 200, 100, 0.2)",
    color: "#e6c98f",
    fontSize: 13,
    lineHeight: 1.4,
  },

  notes: {
    margin: "10px 0 0",
    paddingTop: 10,
    borderTop: "1px solid rgba(255,255,255,0.1)",
    color: "#d7c8a6",
    fontSize: 14,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
  },

  actions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  },

  goldButton: {
    width: "100%",
    padding: 13,
    borderRadius: 15,
    border: "none",
    background: "linear-gradient(180deg, #e4c46f, #c99f43)",
    color: "#101010",
    fontSize: 15,
    fontWeight: 900,
  },

  secondaryButton: {
    width: "100%",
    padding: 13,
    borderRadius: 15,
    border: "1px solid rgba(214,180,106,0.35)",
    background: "rgba(255,255,255,0.06)",
    color: "#f4d98d",
    fontSize: 15,
    fontWeight: 900,
  },

  deleteButton: {
    width: "100%",
    padding: 12,
    marginTop: 12,
    borderRadius: 15,
    border: "1px solid rgba(255,90,90,0.35)",
    background: "rgba(255,90,90,0.13)",
    color: "#ffb6b6",
    fontSize: 15,
    fontWeight: 900,
  },

  empty: {
    textAlign: "center",
    color: "#d8c39b",
    marginTop: 30,
    fontSize: 16,
  },
};

export default Lodges;